import { useState, useMemo, useEffect } from "react";
import debounce from "lodash.debounce";
import { useParamFunctions, useNetworkSearch } from "hooks";
import { PAGINATION_TYPE } from "components/Pagination";
import {
  SORT,
  NETWORK_LIST_SORTING_OPTIONS,
  NETWORK_LIST_SORTING_VALUES,
  NETWORK_PAGE_KEYWORD_SEARCH_SORT,
} from "constants/index";

const KEYS = {
  filters: "f",
  sorts: "s",
  advancedFilters: "af",
  keyword: "keyword",
  sortKey: "sortKey",
  sortDirection: "sortDirection",
};

/**
 * @description Custom hook to manage network controls state with URL parameters
 * @returns {Object} State and setters for network controls
 */
export default function useNetworkControls({ resultsPerPage, initialValues, ...searchProps } = {}) {
  const { paramParse, setParams } = useParamFunctions();
  // Get initial values from URL parameters
  const initialFilters = paramParse(KEYS.filters);
  const initialSorts = paramParse(KEYS.sorts);
  const initialAdvancedFilters = paramParse(KEYS.advancedFilters);

  // Initialize state with URL values
  const [state, setState] = useState({
    keyword: initialFilters?.[KEYS.keyword] ?? "",
    sortKey: initialSorts?.[KEYS.sortKey] ?? initialValues?.sortKey ?? NETWORK_LIST_SORTING_VALUES.showcase_score,
    sortDirection: initialSorts?.[KEYS.sortDirection] ?? initialValues?.sortDirection ?? SORT.desc,
    advancedFilters: initialAdvancedFilters ?? {},
  });

  // For immediate UI updates while debouncing keyword changes
  const [keyword, setKeyword] = useState(state.keyword);
  const hasKeywordSearch = Boolean(state.keyword?.trim());
  const resolvedSort = hasKeywordSearch
    ? NETWORK_PAGE_KEYWORD_SEARCH_SORT
    : {
        sortKey: state.sortKey,
        sortDirection: state.sortDirection,
      };

  // Update URL when keyword changes
  useEffect(() => {
    setParams(KEYS.filters, { [KEYS.keyword]: state.keyword }, true, true);
  }, [state.keyword]);

  // Update URL when advanced filters change
  useEffect(() => {
    setParams(KEYS.advancedFilters, state.advancedFilters, true, true);
  }, [state.advancedFilters]);

  // Update URL when sorts change
  useEffect(() => {
    setParams(
      KEYS.sorts,
      {
        [KEYS.sortKey]: state.sortKey,
        [KEYS.sortDirection]: state.sortDirection,
      },
      true,
      true
    );
  }, [state.sortKey, state.sortDirection]);

  // Debounced keyword setter that updates both state and URL
  const debouncedSetKeyword = useMemo(
    () => debounce((val) => setState((prev) => ({ ...prev, keyword: val })), 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  const handleSearchInputChange = (val) => {
    setKeyword(val);
    debouncedSetKeyword(val);
  };

  const sortingOptions = useMemo(
    () =>
      hasKeywordSearch
        ? NETWORK_LIST_SORTING_OPTIONS
        : NETWORK_LIST_SORTING_OPTIONS.filter(({ value }) => value !== NETWORK_PAGE_KEYWORD_SEARCH_SORT.sortKey),
    [hasKeywordSearch]
  );

  const displayState = hasKeywordSearch ? { ...state, ...resolvedSort } : state;

  // Network search integration
  const searchResults = useNetworkSearch({
    ...searchProps,
    resultsPerPage,
    paginationType: PAGINATION_TYPE.classic,
    advancedFilters: state.advancedFilters,
    keyword: state.keyword,
    sortKey: resolvedSort.sortKey,
    sortDirection: resolvedSort.sortDirection,
    filters: initialValues?.filters,
  });

  return {
    keyword,
    hasKeywordSearch,
    handleSearchInputChange,
    sortingOptions,
    state: displayState,
    setState,
    ...searchResults,
  };
}

export { KEYS };
